import {
  ArrowCounterClockwise,
  ArrowRight,
  Check,
  CheckCircle,
  SkipForward,
  Trash,
} from '@phosphor-icons/react'
import type { ManualDecision, MatchCandidate, MatchResult } from '../../types'
import { formatConfidence, formatMoney, formatNumber } from '../../ui/formatters'

interface ConfirmationPanelProps {
  active: MatchResult | null
  currentIndex: number
  lastDecided: MatchResult | null
  onDecision: (resultId: string, decision: ManualDecision) => void
  onNext: () => void
  onPrevious: () => void
  onUndo: (resultId: string) => void
  total: number
}

function RowFacts({ row }: { row: NonNullable<MatchResult['baselineRow']> }) {
  return (
    <dl className="confirmation-facts">
      <div>
        <dt>项目编码</dt>
        <dd className="tabular-number">{row.itemCode || '缺失'}</dd>
      </div>
      <div>
        <dt>项目名称</dt>
        <dd>{row.itemName || '缺失'}</dd>
      </div>
      <div className="confirmation-facts__wide">
        <dt>项目特征</dt>
        <dd>{row.itemDescription || '缺失'}</dd>
      </div>
      <div>
        <dt>单位</dt>
        <dd>{row.unit || '缺失'}</dd>
      </div>
      <div>
        <dt>来源行</dt>
        <dd className="tabular-number">{row.sourceRowNumber}</dd>
      </div>
      <div>
        <dt>工程量</dt>
        <dd className="tabular-number">{formatNumber(row.quantity)}</dd>
      </div>
      <div>
        <dt>有效合价</dt>
        <dd className="tabular-number">{formatMoney(row.effectiveTotalPrice)}</dd>
      </div>
    </dl>
  )
}

function CandidateCard({
  candidate,
  onConfirm,
}: {
  candidate: MatchCandidate
  onConfirm: () => void
}) {
  const row = candidate.targetRow
  return (
    <article className="candidate-card">
      <div className="candidate-card__heading">
        <div>
          <strong>{row.itemName || '未命名项目'}</strong>
          <span className="tabular-number">{row.itemCode || '无编码'}</span>
        </div>
        <em>{formatConfidence(candidate.score)}</em>
      </div>
      <p className="candidate-description">{row.itemDescription || '无项目特征描述'}</p>
      <div className="candidate-scores" aria-label="字段相似度">
        <span>名称 {formatConfidence(candidate.fieldScores.itemName)}</span>
        <span>特征 {formatConfidence(candidate.fieldScores.itemDescription)}</span>
        <span>单位 {formatConfidence(candidate.fieldScores.unit)}</span>
        <span>编码 {formatConfidence(candidate.fieldScores.itemCode)}</span>
      </div>
      <div className="candidate-card__footer">
        <span>
          {row.unit || '无单位'} · 对比版第 {row.sourceRowNumber} 行
        </span>
        <button className="button button--primary" type="button" onClick={onConfirm}>
          <Check aria-hidden="true" weight="bold" />
          确认匹配
        </button>
      </div>
    </article>
  )
}

export function ConfirmationPanel({
  active,
  currentIndex,
  lastDecided,
  onDecision,
  onNext,
  onPrevious,
  onUndo,
  total,
}: ConfirmationPanelProps) {
  if (!active?.baselineRow) {
    return (
      <div className="empty-state" data-testid="confirmation-empty">
        <div>
          <CheckCircle aria-hidden="true" weight="duotone" />
          <h2>待确认项已处理完毕</h2>
          <p>最终金额统计可以形成。你仍可撤销最近一次人工判断。</p>
          {lastDecided ? (
            <button
              className="button button--secondary"
              type="button"
              onClick={() => onUndo(lastDecided.id)}
            >
              <ArrowCounterClockwise aria-hidden="true" />
              撤销最近判断
            </button>
          ) : null}
        </div>
      </div>
    )
  }

  return (
    <div className="confirmation-layout" data-testid="confirmation-panel">
      <section className="baseline-review-card" aria-labelledby="baseline-review-title">
        <div className="review-card-heading">
          <div>
            <span>基准版待核对项</span>
            <h2 id="baseline-review-title">{active.baselineRow.itemName || '未命名项目'}</h2>
          </div>
          <strong>
            {currentIndex + 1} / {total}
          </strong>
        </div>
        <RowFacts row={active.baselineRow} />
        <div className="match-explanation">
          <strong>进入待确认的原因</strong>
          <p>{active.matchReason}</p>
        </div>
        <div className="review-navigation">
          <button
            className="button button--secondary"
            type="button"
            onClick={onPrevious}
            disabled={currentIndex === 0}
          >
            上一项
          </button>
          <button
            className="button button--secondary"
            type="button"
            onClick={onNext}
            disabled={currentIndex >= total - 1}
          >
            下一项
          </button>
        </div>
      </section>

      <div className="confirmation-arrow" aria-hidden="true">
        <ArrowRight weight="bold" />
      </div>

      <section className="candidate-list" aria-labelledby="candidate-list-title">
        <div className="candidate-list__heading">
          <div>
            <h2 id="candidate-list-title">对比版候选项</h2>
            <p>最多显示三个候选。工程量和价格不作为主要身份依据。</p>
          </div>
          <span>{active.candidates.length} 个候选</span>
        </div>

        <div className="candidate-list__items">
          {active.candidates.slice(0, 3).map((candidate) => (
            <CandidateCard
              key={candidate.targetRow.id}
              candidate={candidate}
              onConfirm={() =>
                onDecision(active.id, {
                  kind: 'confirm-match',
                  targetRowId: candidate.targetRow.id,
                })
              }
            />
          ))}
          {active.candidates.length === 0 ? (
            <p className="inline-message inline-message--warning">没有达到待确认阈值的候选项。</p>
          ) : null}
        </div>

        <div className="candidate-list__actions">
          <button
            className="button button--danger"
            type="button"
            onClick={() => onDecision(active.id, { kind: 'mark-removed' })}
          >
            <Trash aria-hidden="true" />
            判定为基准版删除
          </button>
          <button
            className="button button--secondary"
            type="button"
            onClick={() => onDecision(active.id, { kind: 'skip' })}
          >
            <SkipForward aria-hidden="true" />
            跳过
          </button>
          {lastDecided ? (
            <button
              className="button button--ghost"
              type="button"
              onClick={() => onUndo(lastDecided.id)}
            >
              <ArrowCounterClockwise aria-hidden="true" />
              撤销本次确认
            </button>
          ) : null}
        </div>
      </section>
    </div>
  )
}
